import { supabase } from '../lib/supabase';
import { parseResumeWithAI } from '../lib/openai';
import { parseDocument } from '../utils/documentParser';

class ResumeService {
  // Server action: Parse resume file and extract structured data
  async parseResume(file) {
    try {
      if (!file) {
        throw new Error('No file provided');
      }

      // Validate file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes?.includes(file?.type)) {
        throw new Error('Invalid file type. Only PDF, DOC, and DOCX files are supported.');
      }

      // Extract text from document
      const extractedText = await parseDocument(file);
      
      if (!extractedText) {
        throw new Error('Could not extract text from document');
      }

      // Use AI to parse resume data
      const parsedData = await parseResumeWithAI(extractedText);
      
      if (!parsedData) {
        throw new Error('Failed to parse resume with AI');
      }

      // Extract structured data for candidate update
      const candidateData = {
        name: parsedData?.personal_info?.name || null,
        email: parsedData?.personal_info?.email || null, 
        phone: parsedData?.personal_info?.phone || null,
        location: parsedData?.personal_info?.location || null,
        skills: parsedData?.skills?.map(skill => skill?.name) || [],
        years_of_experience: parsedData?.years_of_experience || 0,
        seniority_level: parsedData?.seniority_level || 'junior'
      };

      return {
        success: true,
        data: candidateData,
        rawParsedData: parsedData,
        extractedText
      };

    } catch (error) {
      return {
        success: false,
        error: error?.message || 'Failed to parse resume'
      };
    }
  }

  // Upload and parse resume document
  async uploadAndParseResume(file, candidateId) {
    try {
      // Enhanced validation
      if (!file) {
        throw new Error('No file provided for upload');
      }

      if (!candidateId) {
        throw new Error('Candidate ID is required for file upload');
      }

      // Validate file type early
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes?.includes(file?.type)) {
        throw new Error('Invalid file type. Only PDF, DOC, and DOCX files are supported.');
      }

      // Validate file size (10MB limit)
      const maxSize = 10 * 1024 * 1024;
      if (file?.size > maxSize) {
        throw new Error('File size too large. Maximum size is 10MB.');
      }

      const fileName = `${candidateId}/${Date.now()}_${file?.name}`;
      
      // Upload file to Supabase Storage with better error handling
      const { data: uploadData, error: uploadError } = await supabase?.storage
        ?.from('resume-documents')
        ?.upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('Supabase storage error:', uploadError);
        
        // Handle specific storage errors
        if (uploadError?.message?.includes('Bucket not found')) {
          throw new Error('Storage configuration error. Please contact support.');
        } else if (uploadError?.message?.includes('Policy')) {
          throw new Error('Storage permission error. Please check your access rights.');
        } else {
          throw new Error(`File upload failed: ${uploadError.message}`);
        }
      }

      if (!uploadData?.path) {
        throw new Error('File upload completed but path is missing');
      }

      // Create resume document record with enhanced error handling
      const documentData = {
        candidate_id: candidateId,
        file_name: file?.name,
        file_path: uploadData?.path,
        file_size: file?.size,
        document_type: this.getDocumentType(file?.name),
        parsing_status: 'processing'
      };

      const { data: docData, error: docError } = await supabase
        ?.from('resume_documents')
        ?.insert(documentData)
        ?.select()
        ?.single();

      if (docError) {
        console.error('Database insert error:', docError);
        
        // Cleanup uploaded file if database insert fails
        await supabase?.storage?.from('resume-documents')?.remove([uploadData?.path]);
        
        if (docError?.message?.includes('foreign key')) {
          throw new Error('Invalid candidate ID. Please refresh the page and try again.');
        } else {
          throw new Error(`Database error: ${docError.message}`);
        }
      }

      // Parse document and update candidate data
      const parseResult = await this.parseResumeDocument(docData?.id, uploadData?.path, candidateId);

      return {
        success: true,
        documentId: docData?.id,
        message: 'Resume uploaded and parsing initiated successfully.',
        candidateData: parseResult?.candidateData
      };
    } catch (error) {
      console.error('Resume upload error:', error);
      return {
        success: false,
        message: error?.message || 'Failed to upload resume. Please try again.'
      };
    }
  }

  // Parse resume document using AI and update candidate data
  async parseResumeDocument(documentId, filePath, candidateId) {
    try {
      // Update status to processing
      await supabase?.from('resume_documents')?.update({ parsing_status: 'processing' })?.eq('id', documentId);

      // Download and parse document
      const { data: fileData, error: downloadError } = await supabase?.storage?.from('resume-documents')?.download(filePath);

      if (downloadError) throw downloadError;

      // Extract text from document
      const extractedText = await parseDocument(fileData, filePath);
      
      if (!extractedText) {
        throw new Error('Could not extract text from document');
      }

      // Use AI to parse resume data
      const parsedData = await parseResumeWithAI(extractedText);

      if (!parsedData) {
        throw new Error('Failed to parse resume with AI');
      }

      // Update candidate with extracted information
      const candidateUpdateData = {};
      
      // Only update if data exists and candidate field is empty/null
      if (parsedData?.personal_info?.name) {
        candidateUpdateData.full_name = parsedData?.personal_info?.name;
      }
      if (parsedData?.personal_info?.email) {
        candidateUpdateData.email = parsedData?.personal_info?.email;
      }
      if (parsedData?.personal_info?.phone) {
        candidateUpdateData.phone = parsedData?.personal_info?.phone;
      }
      if (parsedData?.personal_info?.location) {
        candidateUpdateData.location = parsedData?.personal_info?.location;
      }

      // Update candidate record
      if (Object.keys(candidateUpdateData)?.length > 0) {
        const { error: candidateUpdateError } = await supabase?.from('candidates')?.update(candidateUpdateData)?.eq('id', candidateId);
        
        if (candidateUpdateError) {
          console.warn('Failed to update candidate:', candidateUpdateError?.message);
        }
      }

      // Save parsed data to database
      const { error: insertError } = await supabase?.from('parsed_resume_data')?.insert({
          resume_document_id: documentId,
          candidate_id: candidateId,
          raw_text: extractedText,
          extracted_skills: parsedData?.skills?.map(skill => skill?.name) || [],
          years_of_experience: parsedData?.years_of_experience || 0,
          seniority_level: parsedData?.seniority_level || 'junior',
          work_experience: parsedData?.work_experience || [],
          education: parsedData?.education || [],
          certifications: parsedData?.certifications || [],
          languages: parsedData?.languages || [],
          summary: parsedData?.summary || '',
          parsing_confidence_score: parsedData?.confidence_score || 0.0,
          ai_model_used: 'gpt-4'
        });

      if (insertError) throw insertError;

      // Save individual skills with categories
      if (parsedData?.skills && parsedData?.skills?.length > 0) {
        const skillsData = parsedData?.skills?.map((skill, index) => ({
          candidate_id: candidateId,
          skill_name: skill?.name,
          skill_category: skill?.category || this.categorizeSkill(skill?.name),
          proficiency_level: skill?.proficiency || 'intermediate',
          years_experience: skill?.years_experience || parsedData?.years_of_experience || 0,
          is_primary_skill: index < 5, // First 5 skills as primary
          source: 'ai_parsed'
        }));

        await supabase?.from('candidate_skills')?.insert(skillsData);
      }

      // Update document status to completed
      await supabase?.from('resume_documents')?.update({ parsing_status: 'completed' })?.eq('id', documentId);

      return { 
        success: true, 
        candidateData: {
          ...candidateUpdateData,
          skills: parsedData?.skills?.map(skill => skill?.name) || [],
          years_of_experience: parsedData?.years_of_experience || 0,
          seniority_level: parsedData?.seniority_level || 'junior'
        }
      };
    } catch (error) {
      // Update status to failed
      await supabase?.from('resume_documents')?.update({ parsing_status: 'failed' })?.eq('id', documentId);

      console.error('Resume parsing error:', error);
      return { success: false, error: error?.message };
    }
  }

  // Generate candidate summary using AI
  async generateCandidateSummary(candidateId) {
    try {
      // Get candidate data and parsed resume data
      const [candidateResult, parsedDataResult, skillsResult] = await Promise.all([
        supabase?.from('candidates')?.select('*')?.eq('id', candidateId)?.single(),
        supabase?.from('parsed_resume_data')?.select('*')?.eq('candidate_id', candidateId)?.order('created_at', { ascending: false })?.limit(1)?.single(),
        supabase?.from('candidate_skills')?.select('*')?.eq('candidate_id', candidateId)?.order('is_primary_skill', { ascending: false })?.limit(10)
      ]);

      const candidate = candidateResult?.data;
      const parsedData = parsedDataResult?.data;
      const skills = skillsResult?.data || [];

      if (!candidate) {
        throw new Error('Candidate not found');
      }

      // Generate AI summary
      const experienceYears = parsedData?.years_of_experience || 0;
      const seniorityLevel = parsedData?.seniority_level || 'junior';
      const primarySkills = skills?.filter(s => s?.is_primary_skill)?.slice(0, 5)?.map(s => s?.skill_name) || [];
      const allSkills = skills?.map(s => s?.skill_name) || [];
      
      // Create structured summary
      const experienceBullet = experienceYears > 0 
        ? `• Experience: ${experienceYears} years of professional experience as a ${seniorityLevel}-level professional`
        : `• Experience: ${seniorityLevel?.charAt(0)?.toUpperCase() + seniorityLevel?.slice(1)}-level professional with relevant background`;

      const skillsBullet = primarySkills?.length > 0 
        ? `• Skills: Strong expertise in ${primarySkills?.slice(0, 3)?.join(', ')} with additional proficiency in ${allSkills?.length - 3 > 0 ? `${allSkills?.length - 3}+ technologies` : 'related technologies'}`
        : `• Skills: Technical professional with diverse skill set across multiple domains`;

      const signalsBullet = parsedData?.parsing_confidence_score 
        ? `• Signals: Well-structured professional profile with ${Math.round(parsedData?.parsing_confidence_score * 100)}% data completeness${parsedData?.work_experience?.length > 0 ? ` and ${parsedData?.work_experience?.length} documented work positions` : ''}`
        : `• Signals: Professional candidate profile with structured background and documented experience`;

      const summary = `${experienceBullet}\n${skillsBullet}\n${signalsBullet}`;

      // Save to notes table
      const { data: noteData, error: noteError } = await supabase?.from('notes')?.insert({
          application_id: null, // Will need application_id if linking to specific application
          author_id: null, // Will need current user ID
          org_id: candidate?.org_id,
          body: `AI-Generated Candidate Summary:\n\n${summary}`,
        })?.select()?.single();

      if (noteError) {
        console.warn('Failed to save note:', noteError?.message);
      }

      return {
        success: true,
        summary,
        noteId: noteData?.id || null
      };

    } catch (error) {
      console.error('Error generating candidate summary:', error);
      return {
        success: false,
        error: error?.message || 'Failed to generate candidate summary'
      };
    }
  }

  // Get parsed resume data for candidate
  async getParsedResumeData(candidateId) {
    try {
      const { data, error } = await supabase?.from('parsed_resume_data')?.select(`
          *,
          resume_document:resume_documents(
            file_name,
            file_path,
            document_type,
            parsing_status,
            upload_date
          )
        `)?.eq('candidate_id', candidateId)?.order('created_at', { ascending: false });

      if (error) {
        throw new Error(`Failed to fetch parsed data: ${error.message}`);
      }

      return data || [];
    } catch (error) {
      return [];
    }
  }

  // Get candidate skills
  async getCandidateSkills(candidateId) {
    try {
      const { data, error } = await supabase?.from('candidate_skills')?.select('*')?.eq('candidate_id', candidateId)?.order('is_primary_skill', { ascending: false })?.order('years_experience', { ascending: false });

      if (error) {
        throw new Error(`Failed to fetch skills: ${error.message}`);
      }

      return data || [];
    } catch (error) {
      return [];
    }
  }

  // Calculate job match score
  async calculateJobMatch(candidateId, jobId) {
    try {
      const { data, error } = await supabase?.rpc('calculate_candidate_job_match', {
          candidate_uuid: candidateId,
          job_uuid: jobId
        });

      if (error) {
        throw new Error(`Failed to calculate match: ${error.message}`);
      }

      return data?.[0] || {
        match_score: 0,
        matched_skills: [],
        missing_skills: [],
        experience_gap: 0
      };
    } catch (error) {
      return {
        match_score: 0,
        matched_skills: [],
        missing_skills: [],
        experience_gap: 0
      };
    }
  }

  // Get resume documents for candidate
  async getResumeDocuments(candidateId) {
    try {
      const { data, error } = await supabase?.from('resume_documents')?.select('*')?.eq('candidate_id', candidateId)?.order('created_at', { ascending: false });

      if (error) {
        throw new Error(`Failed to fetch documents: ${error.message}`);
      }

      // Generate signed URLs for private documents
      const documentsWithUrls = await Promise.all(
        (data || [])?.map(async (doc) => {
          try {
            const { data: urlData } = await supabase?.storage?.from('resume-documents')?.createSignedUrl(doc?.file_path, 3600); // 1 hour expiry

            return {
              ...doc,
              downloadUrl: urlData?.signedUrl || null
            };
          } catch {
            return {
              ...doc,
              downloadUrl: null
            };
          }
        })
      );

      return documentsWithUrls;
    } catch (error) {
      return [];
    }
  }

  // Delete resume document
  async deleteResumeDocument(documentId) {
    try {
      // Get document info
      const { data: docData, error: fetchError } = await supabase?.from('resume_documents')?.select('file_path')?.eq('id', documentId)?.single();

      if (fetchError) {
        throw new Error(`Failed to fetch document: ${fetchError.message}`);
      }

      // Delete from storage
      const { error: storageError } = await supabase?.storage?.from('resume-documents')?.remove([docData?.file_path]);

      if (storageError) {
        console.warn('Storage deletion warning:', storageError?.message);
      }

      // Delete from database (cascades to parsed_resume_data)
      const { error: deleteError } = await supabase?.from('resume_documents')?.delete()?.eq('id', documentId);

      if (deleteError) {
        throw new Error(`Failed to delete document: ${deleteError.message}`);
      }

      return { success: true, message: 'Document deleted successfully' };
    } catch (error) {
      return { success: false, message: error?.message };
    }
  }

  // Utility functions
  getDocumentType(fileName) {
    const extension = fileName?.toLowerCase()?.split('.')?.pop();
    const typeMap = {
      'pdf': 'pdf',
      'doc': 'doc',
      'docx': 'docx'
    };
    return typeMap?.[extension] || 'pdf';
  }

  categorizeSkill(skill) {
    const skillCategories = {
      'frontend': ['react', 'vue', 'angular', 'javascript', 'typescript', 'html', 'css', 'sass', 'less'],
      'backend': ['node.js', 'python', 'java', 'c#', 'ruby', 'php', 'go', 'rust', 'express', 'django', 'spring'],
      'database': ['postgresql', 'mysql', 'mongodb', 'redis', 'sqlite', 'oracle', 'sql'],
      'cloud': ['aws', 'azure', 'gcp', 'google cloud', 'docker', 'kubernetes', 'terraform'],
      'mobile': ['react native', 'flutter', 'ios', 'android', 'swift', 'kotlin'],
      'devops': ['docker', 'kubernetes', 'jenkins', 'gitlab', 'github actions', 'terraform', 'ansible'],
      'design': ['figma', 'sketch', 'adobe', 'photoshop', 'illustrator', 'ui/ux'],
      'management': ['agile', 'scrum', 'kanban', 'project management', 'team lead']
    };

    const skillLower = skill?.toLowerCase() || '';
    
    for (const [category, keywords] of Object.entries(skillCategories)) {
      if (keywords?.some(keyword => skillLower?.includes(keyword))) {
        return category;
      }
    }
    
    return 'other';
  }
}

export const resumeService = new ResumeService();